import React, { useEffect, useState } from 'react';
import {
    Box, Text, Button, Input, InputGroup, InputRightElement, IconButton, Select, Switch, FormControl, FormLabel, FormHelperText,
    Skeleton, Collapse, Alert, AlertIcon, Link, useToast
} from '@chakra-ui/react';
import { ExpandMore, ExpandLess, Visibility, VisibilityOff, OpenInNew, CheckCircle, ErrorOutline, RemoveCircleOutline } from '@mui/icons-material';

import AdminLayout from '../../components/admin/AdminLayout';
import { listIntegrations, saveIntegration, testIntegration, refreshPublicSettings } from '../../services/SettingsServices';

const CATEGORY_ORDER = ['Payments', 'Shipping', 'Messaging', 'Customer login', 'Media'];

// Status shown with icon + label, never colour alone
const statusOf = (i) => i.alwaysOn || (i.enabled && i.configured)
    ? { label: 'Connected', icon: CheckCircle, color: '#1E6B3A', bg: '#E8F5EC' }
    : !i.configured
        ? { label: 'Needs setup', icon: ErrorOutline, color: '#9A5B13', bg: '#FFF4E5' }
        : { label: 'Off', icon: RemoveCircleOutline, color: 'blackAlpha.700', bg: 'raano.surface' };

const Integrations = () => {

    const [integrations, setIntegrations] = useState(null);
    const [open, setOpen] = useState(null);

    useEffect(() => { listIntegrations().then(setIntegrations).catch(() => setIntegrations([])); }, []);

    const replace = (updated) => setIntegrations(prev => prev.map(i => i.key === updated.key ? updated : i));

    return (
        <AdminLayout title='Integrations' subtitle='Connect payments, shipping, messaging and login services' >
            <Alert status='info' variant='left-accent' borderRadius='8px' mb={4} fontSize={13} py={2} >
                <AlertIcon />Keys are stored encrypted on your server and never shown again after saving.
            </Alert>
            {integrations === null
                ? Array(4).fill(null).map((_, i) => <Skeleton key={i} height='72px' borderRadius='12px' mb={2} />)
                : CATEGORY_ORDER.filter(c => integrations.some(i => i.category === c)).map(category => (
                    <Box key={category} mb={5} >
                        <Text fontSize={12} fontWeight={700} color='blackAlpha.500' textTransform='uppercase' letterSpacing='1px' mb={2} px={1} >{category}</Text>
                        <Box bg='raano.white' borderRadius='12px' border='1px solid' borderColor='raano.border' overflow='hidden' >
                            {integrations.filter(i => i.category === category).map((integration, idx) => (
                                <IntegrationRow
                                    key={integration.key}
                                    integration={integration}
                                    isOpen={open === integration.key}
                                    first={idx === 0}
                                    onToggle={() => setOpen(open === integration.key ? null : integration.key)}
                                    onSaved={replace}
                                />
                            ))}
                        </Box>
                    </Box>
                ))}
        </AdminLayout>
    )
}

const IntegrationRow = ({ integration, isOpen, first, onToggle, onSaved }) => {

    const toast = useToast();
    const [values, setValues] = useState({});
    const [enabled, setEnabled] = useState(integration.enabled);
    const [reveal, setReveal] = useState({});
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [result, setResult] = useState(null);
    const status = statusOf(integration);
    const StatusIcon = status.icon;

    useEffect(() => {
        if (!isOpen) return;
        setValues(Object.fromEntries(integration.fields.map(f => [f.name, f.type === 'secret' ? '' : (f.value ?? '')])));
        setEnabled(integration.enabled);
        setResult(null);
    }, [isOpen, integration]);

    const dirty = integration.fields.some(f => f.type === 'secret' ? values[f.name] : String(values[f.name] ?? '') !== String(f.value ?? '')) || enabled !== integration.enabled;

    const onSave = async () => {
        setSaving(true);
        try {
            // Blank secret fields mean "keep the saved one"
            const config = Object.fromEntries(Object.entries(values).filter(([name, v]) => {
                const field = integration.fields.find(f => f.name === name);
                return field.type !== 'secret' || v;
            }));
            const updated = await saveIntegration(integration.key, { enabled: integration.alwaysOn ? undefined : enabled, config });
            onSaved(updated);
            refreshPublicSettings();
            toast({ title: `${integration.label} saved`, status: 'success', duration: 1500, position: 'top' });
            if (updated.configured && (updated.enabled || updated.alwaysOn)) runTest();
        } catch (e) {
            toast({ title: 'Could not save', description: e.response?.data?.error || e.message, status: 'error', duration: 3000 });
        }
        setSaving(false);
    };

    const runTest = async () => {
        setTesting(true);
        setResult(null);
        try { setResult(await testIntegration(integration.key)); }
        catch (e) { setResult({ ok: false, message: e.response?.data?.error || e.message }); }
        setTesting(false);
    };

    return (
        <Box borderTop={first ? 'none' : '1px solid'} borderColor='raano.border' >
            <Box as='button' width='100%' textAlign='left' display='flex' alignItems='center' gap={3} px={4} py={3.5} onClick={onToggle} aria-expanded={isOpen} >
                <Box boxSize='40px' borderRadius='8px' bg='raano.black' color='raano.white' display='flex' alignItems='center' justifyContent='center' fontWeight={800} flexShrink={0} >
                    {integration.label[0]}
                </Box>
                <Box flex={1} minWidth={0} >
                    <Text fontWeight={700} >{integration.label}</Text>
                    <Text fontSize={12} color='blackAlpha.600' noOfLines={1} >{integration.description}</Text>
                </Box>
                <Box display='inline-flex' alignItems='center' gap={1} px={2} py={0.5} borderRadius='full' bg={status.bg} color={status.color} flexShrink={0} >
                    <StatusIcon sx={{ fontSize: 14 }} /><Text fontSize={12} fontWeight={700} >{status.label}</Text>
                </Box>
                {isOpen ? <ExpandLess fontSize='small' /> : <ExpandMore fontSize='small' />}
            </Box>

            <Collapse in={isOpen} animateOpacity >
                <Box px={4} pb={4} >
                    {integration.source === 'env' &&
                        <Text fontSize={12} color='blackAlpha.600' mb={3} >Currently using values from the server's .env file. Saving here overrides them.</Text>}

                    {!integration.alwaysOn &&
                        <FormControl display='flex' alignItems='center' justifyContent='space-between' p={3} bg='raano.surface' borderRadius='8px' mb={4} >
                            <FormLabel htmlFor={`on-${integration.key}`} mb={0} fontSize={14} fontWeight={600} >Turn on {integration.label}</FormLabel>
                            <Switch id={`on-${integration.key}`} isChecked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
                        </FormControl>}

                    {integration.fields.map(field => (
                        <FormControl key={field.name} mb={3} isRequired={field.required && !field.hasValue} >
                            <FormLabel fontSize={14} mb={1} >{field.label}</FormLabel>
                            {field.type === 'select' ?
                                <Select value={values[field.name] ?? ''} onChange={(e) => setValues(v => ({ ...v, [field.name]: e.target.value }))} >
                                    {field.options.map(o => <option key={o.value} value={o.value} disabled={o.disabled} >{o.label}</option>)}
                                </Select>
                                : field.type === 'secret' ?
                                    <InputGroup>
                                        <Input
                                            type={reveal[field.name] ? 'text' : 'password'}
                                            autoComplete='off'
                                            placeholder={field.hasValue ? `Saved (${field.hint}). Type to replace` : (field.placeholder || 'Paste key')}
                                            value={values[field.name] ?? ''}
                                            onChange={(e) => setValues(v => ({ ...v, [field.name]: e.target.value }))}
                                        />
                                        <InputRightElement>
                                            <IconButton size='sm' variant='ghost' aria-label='Show key' icon={reveal[field.name] ? <VisibilityOff fontSize='small' /> : <Visibility fontSize='small' />} onClick={() => setReveal(r => ({ ...r, [field.name]: !r[field.name] }))} />
                                        </InputRightElement>
                                    </InputGroup>
                                    : <Input type={field.type === 'number' ? 'number' : 'text'} inputMode={field.type === 'number' ? 'decimal' : undefined} placeholder={field.placeholder} value={values[field.name] ?? ''} onChange={(e) => setValues(v => ({ ...v, [field.name]: e.target.value }))} />}
                            {field.help && <FormHelperText fontSize={12} >{field.help}</FormHelperText>}
                        </FormControl>
                    ))}

                    {integration.docsUrl &&
                        <Link href={integration.docsUrl} isExternal fontSize={13} color='raano.black' display='inline-flex' alignItems='center' gap={1} mb={3} >
                            Where do I find these? <OpenInNew sx={{ fontSize: 14 }} />
                        </Link>}

                    {result &&
                        <Alert status={result.ok ? 'success' : 'error'} borderRadius='8px' fontSize={13} py={2} mb={3} >
                            <AlertIcon />{result.message}
                        </Alert>}

                    <Box display='flex' gap={2} >
                        <Button flex={1} variant='wipe' height='40px' px='18px' fontSize='14px' isLoading={testing} loadingText='Testing…' isDisabled={dirty || !integration.configured} onClick={runTest} >Test connection</Button>
                        <Button flex={1} variant='ink' height='40px' px='18px' fontSize='14px' isLoading={saving} isDisabled={!dirty} onClick={onSave} >Save</Button>
                    </Box>
                    {dirty && integration.configured && <Text fontSize={11} color='blackAlpha.500' mt={1} textAlign='center' >Save first, then test.</Text>}
                </Box>
            </Collapse>
        </Box>
    )
}

export default Integrations;
